package example.shadcn_kotlin.ui.pages.docs

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobwebx.markdown.*
import example.shadcn_kotlin.ui.components.*

@Page
@Composable
fun ThemingPage() {
    CompositionLocalProvider(LocalMarkdownContext provides MarkdownContext("docs/theming.md", mapOf("title" to listOf("Theming"), "description" to listOf("Using CSS Variables or Tailwind CSS for theming.")))) {
        example.shadcn_kotlin.ui.components.layouts.MDLayout {
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("We use CSS variables for styling. This allows you to easily change the colors of components without having to update class names.")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("CSS variables must be defined without the color space function")
                }
                org.jetbrains.compose.web.dom.Text(". See the ")
                org.jetbrains.compose.web.dom.A("https://tailwindcss.com/docs/customizing-colors#using-css-variables", { classes("font-medium","underline","underline-offset-4") }) {
                    org.jetbrains.compose.web.dom.Text("Tailwind CSS documentation")
                }
                org.jetbrains.compose.web.dom.Text(" for more information.")
            }
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("convention")
 }) {
                org.jetbrains.compose.web.dom.Text("Convention")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("We use a simple ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("background")
                }
                org.jetbrains.compose.web.dom.Text(" and ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("foreground")
                }
                org.jetbrains.compose.web.dom.Text(" convention for colors. The ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("background")
                }
                org.jetbrains.compose.web.dom.Text(" variable is used for the background color of the component and the ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("foreground")
                }
                org.jetbrains.compose.web.dom.Text(" variable is used for the text color.")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("The ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("background")
                }
                org.jetbrains.compose.web.dom.Text(" suffix can be omitted if the variable is used for the background color of the component.")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("Given the following CSS variables:")
            }
            CodeBlock("""--primary: 222.2 47.4% 11.2%;
--primary-foreground: 210 40% 98%;
""", lang= "css", highlight= "")
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("The ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("background")
                }
                org.jetbrains.compose.web.dom.Text(" color of the following component will be ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("hsl(var(--primary))")
                }
                org.jetbrains.compose.web.dom.Text(" and the ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("foreground")
                }
                org.jetbrains.compose.web.dom.Text(" color will be ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("hsl(var(--primary-foreground))")
                }
                org.jetbrains.compose.web.dom.Text(".")
            }
            CodeBlock("""div {
    className = ClassName("bg-primary text-primary-foreground")
    + "Hello"
}
""", lang= "kotlin", highlight= "")
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("css-variables")
 }) {
                org.jetbrains.compose.web.dom.Text("CSS Variables")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("Here's the list of variables available for customization:")
            }
            CodeBlock("""--background: 0 0% 100%;
--foreground: 222.2 47.4% 11.2%;
""", lang= "css", highlight= "", title= "Default background color of <body />...etc")
            CodeBlock("""--muted: 210 40% 96.1%;
--muted-foreground: 215.4 16.3% 46.9%;
""", lang= "css", highlight= "", title= "Muted backgrounds such as <TabsList />, <Skeleton /> and <Switch />")
            CodeBlock("""--card: 0 0% 100%;
--card-foreground: 222.2 47.4% 11.2%;
""", lang= "css", highlight= "", title= "Background color for <Card />")
            CodeBlock("""--popover: 0 0% 100%;
--popover-foreground: 222.2 47.4% 11.2%;
""", lang= "css", highlight= "", title= "Background color for popovers such as <DropdownMenu />, <HoverCard />, <Popover />")
            CodeBlock("""--border: 214.3 31.8% 91.4%;
""", lang= "css", highlight= "", title= "Default border color")
            CodeBlock("""--input: 214.3 31.8% 91.4%;
""", lang= "css", highlight= "", title= "Border color for inputs such as <Input />, <Select />, <Textarea />")
            CodeBlock("""--primary: 222.2 47.4% 11.2%;
--primary-foreground: 210 40% 98%;
""", lang= "css", highlight= "", title= "Primary colors for <Button />")
            CodeBlock("""--secondary: 210 40% 96.1%;
--secondary-foreground: 222.2 47.4% 11.2%;
""", lang= "css", highlight= "", title= "Secondary colors for <Button />")
            CodeBlock("""--accent: 210 40% 96.1%;
--accent-foreground: 222.2 47.4% 11.2%;
""", lang= "css", highlight= "", title= "Used for accents such as hover effects on <DropdownMenuItem>, <SelectItem>...etc")
            CodeBlock("""--destructive: 0 100% 50%;
--destructive-foreground: 210 40% 98%;
""", lang= "css", highlight= "", title= "Used for destructive actions such as <Button variant=")
            CodeBlock("""--ring: 215 20.2% 65.1%;
""", lang= "css", highlight= "", title= "Used for focus ring")
            CodeBlock("""--radius: 0.5rem;
""", lang= "css", highlight= "", title= "Border radius for card, input and buttons")
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("adding-new-colors")
 }) {
                org.jetbrains.compose.web.dom.Text("Adding new colors")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("To add new colors, you need to add them to your CSS file and to your ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("tailwind.config.js")
                }
                org.jetbrains.compose.web.dom.Text(" file.")
            }
            CodeBlock(""":root {
  --warning: 38 92% 50%;
  --warning-foreground: 48 96% 89%;
}

.dark {
  --warning: 48 96% 89%;
  --warning-foreground: 38 92% 50%;
}
""", lang= "css", highlight= "", title= "app/globals.css")
            CodeBlock("""module.exports = {
  theme: {
    extend: {
      colors: {
        warning: "hsl(var(--warning))",
        "warning-foreground": "hsl(var(--warning-foreground))",
      },
    },
  },
}
""", lang= "js", highlight= "5-6", title= "tailwind.config.js")
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("You can now use the ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("warning")
                }
                org.jetbrains.compose.web.dom.Text(" utility class in your components.")
            }
            CodeBlock("""div {
    className = ClassName("bg-warning text-warning-foreground")
}
""", lang= "kotlin", highlight= "")
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("other-color-formats")
 }) {
                org.jetbrains.compose.web.dom.Text("Other color formats")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("I recommend using ")
                org.jetbrains.compose.web.dom.A("https://www.smashingmagazine.com/2021/07/hsl-colors-css/", { classes("font-medium","underline","underline-offset-4") }) {
                    org.jetbrains.compose.web.dom.Text("HSL colors")
                }
                org.jetbrains.compose.web.dom.Text(" for theming but you can also use other color formats if you prefer.")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("See the ")
                org.jetbrains.compose.web.dom.A("https://tailwindcss.com/docs/customizing-colors#using-css-variables", { classes("font-medium","underline","underline-offset-4") }) {
                    org.jetbrains.compose.web.dom.Text("Tailwind CSS documentation")
                }
                org.jetbrains.compose.web.dom.Text(" for more information on using ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("rgb")
                }
                org.jetbrains.compose.web.dom.Text(", ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("rgba")
                }
                org.jetbrains.compose.web.dom.Text(" or ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("hsl")
                }
                org.jetbrains.compose.web.dom.Text(" colors.")
            }
        }
    }
}
